import { countries } from 'countries-list'
import { useFormContext } from 'react-hook-form'

import { Form } from '@tribeplatform/react-sdk/components'
import {
  useBillingDetails,
  useUpdateBillingDetails,
  useVatTypes,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { EMAIL_PATTERN } from '../../../../lib/validator.utils'
import { states } from '../states'

const StateInput = () => {
  const { watch } = useFormContext()
  const country = watch('address.country')

  if (!['US', 'CA'].includes(country)) return null

  return (
    <Form.Select
      label="State or province"
      name="address.state"
      items={states(country).map(state => ({ value: state, text: state }))}
      validation={{
        required: 'State or province is required',
      }}
    />
  )
}

const VatTypesInput = () => {
  const { data: vatTypes } = useVatTypes()
  const { watch } = useFormContext()
  const vatType = watch('vat.vatType')

  const vatIdPlaceholder = `ex. ${
    vatTypes?.find(vt => vt?.key === vatType)?.placeholder
  }`

  return (
    <>
      <Form.Select
        label="VAT Type"
        name="vat.vatType"
        items={vatTypes?.map(vatType => ({
          value: vatType.key,
          text: vatType.text,
        }))}
      />
      {vatType && (
        <Form.Input
          label="VAT ID"
          name="vat.vatId"
          placeholder={vatIdPlaceholder}
          validation={{
            required: 'VAT ID is required',
          }}
        />
      )}
    </>
  )
}

export const ChangeBillingDetailsModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const { data: billingDetails } = useBillingDetails()
  const { mutateAsync: updateBillingDetails, isLoading } =
    useUpdateBillingDetails()
  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header title="Change Billing Details" />
      <Modal.Content>
        <Form
          defaultValues={{
            ...billingDetails,
            vat: {
              vatId: billingDetails?.vat?.vatId,
              vatType: billingDetails?.vat?.vatType,
            },
          }}
          onSubmit={async data => {
            const { companyName, billingEmail, address, vat } = data
            try {
              await updateBillingDetails({
                input: {
                  billingEmail,
                  companyName,
                  address,
                  vat: vat?.vatType ? vat : undefined,
                },
              })
              toast({
                title: 'Billing details updated',
                status: 'success',
              })
            } catch {
              toast({
                title: 'Unable to update billing details',
                status: 'error',
              })
            }
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              label="Company name"
              placeholder="Optional"
              name="companyName"
              autoComplete="organization"
            />
            <Form.Input
              label="Billing email"
              name="billingEmail"
              validation={{
                required: 'Email is required',
                pattern: {
                  value: EMAIL_PATTERN,
                  message: 'Invalid email address',
                },
              }}
              autoComplete="email"
              autoCapitalize="none"
            />
            <Form.Select
              name="address.country"
              label="Country or region"
              items={Object.entries(countries).map(([code, country]) => ({
                value: code,
                text: (
                  <div>
                    {country.emoji} {country.name}
                  </div>
                ),
              }))}
              validation={{
                required: 'Country or region is required',
              }}
            />
            <StateInput />
            <Form.Input
              name="address.streetAddress"
              label="Street address"
              autoComplete="street-address"
              validation={{
                required: 'Street address is required',
              }}
            />
            <Form.Input
              name="address.city"
              label="City"
              autoComplete="address-level2"
              validation={{
                required: 'City is required',
              }}
            />
            <Form.Input
              name="address.postalCode"
              label="Postal code"
              autoComplete="postal-code"
              placeholder="Optional"
            />
            <VatTypesInput />
            <Form.Actions>
              <Button type="submit" loading={isLoading}>
                Update
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
